'use client';

import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { useTranslation } from 'next-i18next';
import { bjToast } from '@components/common';

const OUTPUT_SAMPLE_RATE = 24000;
const TAB_ID = Math.random().toString(36).slice(2);

const WORKLET_SOURCE = `
const TARGET_RATE = 16000;
const BUFFER_SIZE = (TARGET_RATE * 100) / 1000;
class PcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Int16Array(BUFFER_SIZE);
    this.offset = 0;
    this.ratio = sampleRate / TARGET_RATE;
    this.fractionalIndex = 0;
  }
  process(inputs) {
    const input = inputs[0]?.[0];
    if (!input) return true;
    for (let i = 0; i < input.length; i++) {
      this.fractionalIndex += 1;
      if (this.fractionalIndex >= this.ratio) {
        this.fractionalIndex -= this.ratio;
        const sample = Math.max(-1, Math.min(1, input[i]));
        this.buffer[this.offset++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        if (this.offset >= BUFFER_SIZE) {
          this.port.postMessage(this.buffer.slice(0));
          this.offset = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PcmProcessor);
`;

const DEFAULT_PROMPT_PARTS = [
  'bestie_chat_prompt',
  'bestie_tool_execution_rules',
  'bestie_structured_content_handling',
];

export interface GeminiVoiceSession {
  wssUrl: string;
  threadId: string;
  /**
   * null means the backend deliberately sends no prompt override, so the agent runs on
   * its own definition default prompt. The `prompt` key is then omitted from WS setup.
   */
  prompt: { parts: string[]; variables?: Record<string, unknown> } | null;
  config?: Record<string, unknown> | null;
  extractionSchema?: unknown;
}

export interface GeminiVoiceOptions {
  agentName?: string;
  promptParts?: string[];
  firstMessage?: string;
  maxRetries?: number;
  /** Pre-fetched backend session. When provided, skips the signed-url fetch and uses backend-owned prompt/config. */
  session?: GeminiVoiceSession;
  /**
   * Lazily fetch the backend session at connect time (on click), NOT on mount.
   * Preferred over `session` for backend-owned sessions: the signed WSS URL is short-lived
   * (e.g. 60s), so fetching just-in-time avoids an expired URL when the user lingers on the page.
   */
  fetchSession?: () => Promise<GeminiVoiceSession>;
}

export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface GeminiVoiceState {
  status: VoiceStatus;
  error: string | null;
}

export interface GeminiVoiceControls {
  start: () => Promise<void>;
  stop: () => void;
}

function int16ToBase64(buf: Int16Array): string {
  const bytes = new Uint8Array(buf.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function fetchSignedUrl(): Promise<{ wss_url: string; thread_id: string; expires_in: number }> {
  const res = await fetch('/api/agent/invoke/voice/signed-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Signed URL request failed: ${res.status}`);
  }
  const data = await res.json() as { wss_url?: string; thread_id: string; expires_in: number };
  if (!data.wss_url) {
    throw new Error('No wss_url in signed URL response');
  }
  return data as { wss_url: string; thread_id: string; expires_in: number };
}

export function useGeminiVoice(options: GeminiVoiceOptions = {}): GeminiVoiceState & GeminiVoiceControls {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const setStatusTracked = useCallback((s: VoiceStatus) => {
    statusRef.current = s;
    setStatus(s);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const wsRef = useRef<WebSocket | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const activeRef = useRef(false);
  const sessionRef = useRef(0);
  const statusRef = useRef<VoiceStatus>('idle');
  const retryCountRef = useRef(0);
  // Session fetched once per start() cycle and reused across reconnect retries,
  // so retries don't re-POST the backend session endpoint on every attempt.
  const sessionDataRef = useRef<GeminiVoiceSession | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);

  const playChunk = useCallback((pcmBytes: ArrayBuffer) => {
    if (!playbackCtxRef.current) {
      playbackCtxRef.current = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
      nextStartTimeRef.current = 0;
    }
    const ctx = playbackCtxRef.current;
    const int16 = new Int16Array(pcmBytes);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
    }
    const buffer = ctx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
    buffer.getChannelData(0).set(float32);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const now = ctx.currentTime;
    const start = Math.max(now, nextStartTimeRef.current);
    source.start(start);
    nextStartTimeRef.current = start + buffer.duration;
  }, []);

  const cleanup = useCallback(() => {
    activeRef.current = false;
    sessionRef.current += 1;
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    playbackCtxRef.current?.close().catch(() => undefined);
    playbackCtxRef.current = null;
    nextStartTimeRef.current = 0;
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setStatusTracked('disconnected');
    setError(null);
  }, [cleanup, setStatusTracked]);

  const start = useCallback(async () => {
    if (activeRef.current) return;
    activeRef.current = true;
    retryCountRef.current = 0;
    sessionDataRef.current = null;
    const mySession = sessionRef.current;
    setStatusTracked('connecting');
    setError(null);
    channelRef.current?.postMessage({ type: 'session-started', tabId: TAB_ID });

    const micCtx = new AudioContext();
    audioCtxRef.current = micCtx;

    const workletBlob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
    const workletBlobUrl = URL.createObjectURL(workletBlob);
    const workletPromise = Promise.race([
      micCtx.audioWorklet.addModule(workletBlobUrl),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('AudioWorklet load timeout')), 8000)),
    ]);
    // If an early-return path (mic denied, session torn down) closes the AudioContext before
    // we await this below, addModule rejects with AbortError. Swallow it on a detached chain so
    // it never surfaces as an unhandled rejection; the `await workletPromise` below still drives
    // control flow on the live path. Common in dev StrictMode (mount → cleanup → remount).
    workletPromise.catch(() => undefined);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        bjToast.error(t('audio-interview.microphone-permission.denied.description'));
      }
      setError('Microphone access denied');
      setStatusTracked('error');
      cleanup();
      return;
    }

    if (sessionRef.current !== mySession) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    micStreamRef.current = stream;

    try {
      await workletPromise;
    } catch {
      setError('Failed to load audio processor');
      setStatusTracked('error');
      cleanup();
      return;
    } finally {
      URL.revokeObjectURL(workletBlobUrl);
    }

    if (micCtx.state === 'suspended') {
      await micCtx.resume();
    }

    if (sessionRef.current !== mySession) {
      return;
    }

    const micSource = micCtx.createMediaStreamSource(stream);
    const workletNode = new AudioWorkletNode(micCtx, 'pcm-processor');
    workletNodeRef.current = workletNode;
    micSource.connect(workletNode);
    workletNode.connect(micCtx.destination);

    const connect = async () => {
      if (sessionRef.current !== mySession) { return; }

      let wsUrl: string;
      let threadId: string;
      let setupPrompt: { parts: string[]; variables?: Record<string, unknown> } | null;
      let setupConfig: Record<string, unknown>;
      let setupExtractionSchema: unknown;

      let prebuiltSession = optionsRef.current.session ?? sessionDataRef.current ?? undefined;
      if (!prebuiltSession && optionsRef.current.fetchSession) {
        // Lazily fetch the backend session NOW (on connect) so the short-lived WSS URL is fresh.
        // Cache it for this start() cycle so reconnect retries reuse it instead of re-POSTing.
        try {
          prebuiltSession = await optionsRef.current.fetchSession();
        } catch {
          setError('Failed to fetch voice session');
          setStatusTracked('error');
          cleanup();
          return;
        }
        if (sessionRef.current !== mySession) { return; }
        sessionDataRef.current = prebuiltSession;
      }

      if (prebuiltSession) {
        // Backend-provided session: skip signed-url fetch, use backend's wssUrl + prompt
        wsUrl = prebuiltSession.wssUrl;
        threadId = prebuiltSession.threadId;
        // A null prompt means the backend deliberately sends no override, so the agent
        // definition default prompt is used. Any object here — even {} — would count as an
        // override, so it has to stay null all the way to the setup message.
        if (prebuiltSession.prompt === null) {
          setupPrompt = null;
        } else {
          // Only forward `variables` when it's a non-empty object. The backend (PHP) serializes
          // empty variables as `[]` (a JSON array), and the agent does a dict op on it — sending
          // `variables: []` breaks setup and the socket closes. Legacy omitted the key entirely.
          const rawVars = prebuiltSession.prompt.variables;
          const hasVars = !!rawVars && !Array.isArray(rawVars) && Object.keys(rawVars).length > 0;
          setupPrompt = hasVars
            ? { parts: prebuiltSession.prompt.parts, variables: rawVars }
            : { parts: prebuiltSession.prompt.parts };
        }
        setupConfig = {
          ...(prebuiltSession.config ?? {}),
          ...(optionsRef.current.firstMessage ? { first_message: optionsRef.current.firstMessage } : {}),
        };
        setupExtractionSchema = prebuiltSession.extractionSchema;
      } else {
        // Legacy path: fetch signed URL from the local Next.js route
        let signedUrlData: { wss_url: string; thread_id: string; expires_in: number };
        try {
          signedUrlData = await fetchSignedUrl();
        } catch {
          setError('Failed to fetch voice session URL');
          setStatusTracked('error');
          cleanup();
          return;
        }

        if (sessionRef.current !== mySession) { return; }

        wsUrl = signedUrlData.wss_url;
        threadId = signedUrlData.thread_id;
        setupPrompt = { parts: optionsRef.current.promptParts ?? DEFAULT_PROMPT_PARTS };
        setupConfig = {
          input_audio_transcription: true,
          output_audio_transcription: true,
          ...(optionsRef.current.firstMessage ? { first_message: optionsRef.current.firstMessage } : {}),
        };
        setupExtractionSchema = undefined;
      }

      if (sessionRef.current !== mySession) { return; }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.binaryType = 'arraybuffer';

      // Safari can hang on ws.onopen indefinitely — bail after 15s
      const wsConnectTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close(4000, 'connect timeout');
        }
      }, 15000);

      workletNode.port.onmessage = (e: MessageEvent<Int16Array>) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
          realtime_input: {
            media_chunks: [{ mime_type: 'audio/pcm;rate=16000', data: int16ToBase64(e.data) }],
          },
        }));
      };

      ws.onopen = () => {
        clearTimeout(wsConnectTimeout);
        if (sessionRef.current !== mySession) { ws.close(); return; }
        ws.send(JSON.stringify({
          thread_id: threadId,
          ...(setupPrompt !== null ? { prompt: setupPrompt } : {}),
          config: setupConfig,
          ...(setupExtractionSchema !== undefined ? { extraction_schema: setupExtractionSchema } : {}),
        }));
        setStatusTracked('connected');
      };

      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          // First audio = the conversation actually started; only now is it safe to
          // refill the retry budget. Resetting on onopen instead caused an infinite
          // reconnect loop when the agent opened then immediately closed the socket.
          retryCountRef.current = 0;
          playChunk(event.data);
        }
      };

      ws.onerror = () => {};

      ws.onclose = (evt) => {
        clearTimeout(wsConnectTimeout);
        if (wsRef.current !== ws) return;
        wsRef.current = null;

        const maxRetries = optionsRef.current.maxRetries ?? 3;
        const isUnexpected = evt.code !== 1000 && evt.code !== 4401;
        if (isUnexpected && sessionRef.current === mySession && retryCountRef.current < maxRetries) {
          const delay = (2 ** retryCountRef.current) * 1000;
          retryCountRef.current += 1;
          setStatusTracked('connecting');
          setTimeout(() => { void connect(); }, delay);
        } else {
          retryCountRef.current = 0;
          // Surface the close code/reason — if the conversation never started, the agent
          // closed the socket right after setup and this is the only clue why.
          if (isUnexpected) {
            setError(`Voice disconnected (code ${evt.code}${evt.reason ? `: ${evt.reason}` : ''})`);
            setStatusTracked('error');
          } else {
            setStatusTracked('disconnected');
          }
          cleanup();
        }
      };
    };

    await connect();
  }, [cleanup, playChunk, setStatusTracked, t]);

  useEffect(() => () => { cleanup(); }, [cleanup]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('bj-voice-session');
    channelRef.current = channel;
    channel.onmessage = (e: MessageEvent<{ type: string; tabId?: string }>) => {
      // Only stop if from a different tab — same-page instances share TAB_ID
      if (e.data?.type === 'session-started' && e.data?.tabId !== TAB_ID && activeRef.current) {
        cleanup();
        setStatusTracked('idle');
        setError(null);
      }
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [cleanup, setStatusTracked]);

  return {
    status, error, start, stop,
  };
}
